import get from 'lodash/get'
import Color from 'color'

export const useSiteHead = () => {
  const { $site, $i18n } = useNuxtApp()

  const style = computed(() => {
    const rootVars = <Record<string, any>>{}

    const colours = $site.colors

    const format = (key, { lighten, darken } = {}) => {
      const colour = get(colours, key)
      const trimmed = colour.trim()

      try {
        let output = Color(trimmed)

        if (lighten) {
          output = output.lighten(lighten)
        }

        if (darken) {
          output = output.darken(darken)
        }

        return output
          .rgb()
          .array()
          .join(' ')
      } catch (e) {
        return '255 0 0'
      }
    }

    rootVars['--bookapp-hero-background-color'] = $site.colors.header.background
    rootVars['--bookapp-hero-background-opacity'] = $site.colors.header.opacity
    rootVars['--bookapp-hero-background-image-large'] = $site.images.heroImageCssUrl('large')
    rootVars['--bookapp-hero-background-image-medium'] = $site.images.heroImageCssUrl('medium')
    rootVars['--bookapp-hero-background-image-small'] = $site.images.heroImageCssUrl('small')
    rootVars['--bookapp-hero-logo-image'] = $site.images.logoImageCssUrl('large')
    rootVars['--bookapp-hero-text-color'] = $site.colors.header.text

    rootVars['--bookapp-content-tint1-color'] = $site.colors.content['background-1']
    rootVars['--bookapp-content-tint2-color'] = $site.colors.content['background-2']
    rootVars['--bookapp-content-text-color'] = $site.colors.content.text

    rootVars['--bookapp-action-tint-color'] = $site.colors.action.background
    rootVars['--bookapp-action-text-color'] = $site.colors.action.text
    rootVars['--bookapp-action-tint-color-lighten-75'] = format('action.background', { lighten: 0.75 })
    rootVars['--bookapp-action-tint-color-lighten-50'] = format('action.background', { lighten: 0.5 })
    rootVars['--bookapp-action-tint-color-lighten-25'] = format('action.background', { lighten: 0.25 })

    // Contrast
    const action = Color($site.colors.action.background?.trim())
    const background = Color($site.colors.content?.['background-2'].trim())
    const contrast = action.contrast(background)

    // Text
    rootVars['--bookapp-text-primary'] = contrast <= 2 ? format('content.text') : format('action.background')
    rootVars['--bookapp-text-secondary'] = format('content.text')

    // Background
    rootVars['--bookapp-bg-primary'] = format('content.background-1')
    rootVars['--bookapp-bg-secondary'] = format('content.background-2')

    // Border colour
    rootVars['--bookapp-border-primary'] = format('content.background-1')
    rootVars['--bookapp-border-secondary'] = format('content.background-2')
    rootVars['--bookapp-border-action'] = format('action.background')
    rootVars['--bookapp-border-color'] = format('content.background-2', { lighten: 0.75 })

    // Action
    rootVars['--bookapp-bg-action'] = format('action.background')
    rootVars['--bookapp-text-action'] = format('action.text')

    // Border size/radius
    rootVars['--bookapp-border-width'] = '1px'
    rootVars['--bookapp-border-radius'] = get(colours, 'border.radius') !== null ? get(colours, 'border.radius') + 'rem' : '0.25rem'
    // rootVars['--bookapp-border-color'] = get(colours, 'border.colour') ? format('border.colour') : 'rgba(0,0,0,.125)'

    if (get(colours, 'border.colour')) {
      const border = Color(get(colours, 'border.colour').trim())
      const rgb = border.array().join(',')

      rootVars['--bookapp-border-color'] = `rgba(${rgb},1)`
    } else {
      rootVars['--bookapp-border-color'] = 'rgba(0,0,0,.125)'
    }

    // Hero
    rootVars['--bookapp-bg-hero'] = format('header.background')
    rootVars['--bookapp-opacity-hero'] = $site.colors.header.opacity

    // Font
    rootVars['--bookapp-font-display'] = ['Lato'] // $site.fonts.family
    rootVars['--bookapp-font-sans'] = ['Lato']

    let styleString = ':root { '

    const keys = Object.keys(rootVars)
    for (const key of keys) {
      styleString += key + ': ' + rootVars[key] + ';'
    }

    styleString += ' }'

    return [{ hid: 'root-styles', innerHTML: styleString, type: 'text/css' }]
  })

  const lang = computed(() => {
    const locale = $site.location.locale
    let lang = null
    if (locale) {
      const localeParts = locale.split('_')
      if (localeParts.length > 0) {
        lang = localeParts[0]
      }
    }
  })

  const i18nHead = useLocaleHead({
    dir: true
  })

  const scripts = computed(() => {
    const scripts = []

    if (!$site.isEmbed) {
      const contactData = $site.tabs.contact

      const data = {
        '@context': 'http://schema.org',
        '@type': 'LocalBusiness',
        name: (contactData && contactData.contact.business) ? contactData.contact.business : $site.location.title
      }

      if ($site.images.logo) {
        data.image = $site.images.logo.large
      }

      if (contactData) {
        if (contactData.contact.phone) {
          data.telephone = contactData.contact.phone
        }

        if (contactData.contact.website) {
          data.url = contactData.contact.website
        }

        if (contactData.address) {
          data.address = {
            '@type': 'PostalAddress',
            streetAddress: contactData.address.street,
            addressLocality: contactData.address.city,
            addressRegion: contactData.address.region,
            postalCode: contactData.address.postcode,
            addressCountry: contactData.address.country
          }

          data.geo = {
            '@type': 'GeoCoordinates',
            latitude: contactData.address.coord.lat,
            longitude: contactData.address.coord.long
          }
        }
      }

      if ($site.tabs.review) {
        if ($site.tabs.review.stats.count > 0) {
          data.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: $site.tabs.review.stats.average,
            reviewCount: $site.tabs.review.stats.count,
            bestRating: 5,
            worstRating: 1
          }
        }

        if ($site.tabs.review.latest.length > 0) {
          const latestReviews = []

          for (let i = 0; i < $site.tabs.review.latest.length; i++) {
            const review = $site.tabs.review.latest[i]

            latestReviews.push({
              '@type': 'Review',
              author: {
                '@type': 'Person',
                name: (review.customer) ? review.customer : $i18n.t('general.anonymous'),
              },
              datePublished: review.date,
              reviewBody: review.comment,
              reviewRating: {
                '@type': 'Rating',
                ratingValue: (review.rating) ? review.rating : ((review.recommend) ? 5 : 1),
                bestRating: 5,
                worstRating: 1
              }
            })
          }

          data.review = latestReviews
        }
      }

      const sameAs = []
      if ($site.tabs.facebook && $site.tabs.facebook.pageID) {
        sameAs.push('https://facebook.com/' + $site.tabs.facebook.pageID)
      }
      if ($site.tabs.twitter && $site.tabs.twitter.username) {
        sameAs.push('https://twitter.com/' + $site.tabs.twitter.username)
      }

      if (sameAs.length > 0) {
        data.sameAs = sameAs
      }

      scripts.push({ hid: 'ldjson-schema', innerHTML: JSON.stringify(data), type: 'application/ld+json' })
    }

    return scripts
  })

  const meta = computed(() => {
    const meta = [
      { name: 'theme-color', content: $site.colors.action.background },
      { name: 'apple-mobile-web-app-title', content: $site.location.title },
      { name: 'application-name', content: $site.location.title },
      { name: 'og:site_name', content: $site.location.title }
    ]

    if ($site.settings?.bookappDescription) {
      meta.push({ name: 'description', content: $site.settings.bookappDescription })
      meta.push({ name: 'og:description', content: $site.settings.bookappDescription })
      meta.push({ name: 'twitter:description', content: $site.settings.bookappDescription })
    }

    if ($site.settings?.bookappDescription) {

    }

    if ($site.settings?.indexingEnabled === false) {
      meta.push({ name: 'robots', content: 'noindex' })
    }

    if ($site.integrations.b6359c5ca743483880dee4ffbae2fe95 && $site.integrations.b6359c5ca743483880dee4ffbae2fe95.enabled && $site.integrations.b6359c5ca743483880dee4ffbae2fe95.data && $site.integrations.b6359c5ca743483880dee4ffbae2fe95.data.domainVerificationCode) {
      meta.push({ name: 'facebook-domain-verification', content: $site.integrations.b6359c5ca743483880dee4ffbae2fe95.data.domainVerificationCode })
    }

    if (openGraphProperties.value) {
      const keys = Object.keys(openGraphProperties.value)
      for (const key of keys) {
        meta.push({ property: key, content: openGraphProperties.value[key] })
      }
    }

    if (twitterCardProperties.value) {
      const keys = Object.keys(twitterCardProperties.value)
      for (const key of keys) {
        meta.push({ name: key, content: twitterCardProperties.value[key] })
      }
    }

    return meta
  })

  const link = computed(() => {
    const link = []

    const icons = $site.images?.icon

    if (icons) {
      if (icons['180']) { link.push({ rel: 'apple-touch-icon', href: icons['180'] }) }

      if (icons['16']) { link.push({ rel: 'icon', sizes: '16x16', href: icons['16'] }) }
      if (icons['32']) { link.push({ rel: 'icon', sizes: '32x32', href: icons['32'] }) }
      if (icons['96']) { link.push({ rel: 'icon', sizes: '96x96', href: icons['96'] }) }
      if (icons['192']) { link.push({ rel: 'icon', sizes: '192x192', href: icons['192'] }) }
    } else {
      link.push({ rel: 'icon', type: 'image/png', href: "/img/favicon-16x16.png" })
      link.push({ rel: 'icon', type: 'image/png', href: "/img/favicon-32x32.png" })
    }

    return link
  })

  const openGraphProperties = computed(() => {
    const contactData = $site.tabs.contact

    const properties = {
      'og:site_name': 'BookApp',
      'og:type': 'website',
      'og:title': (contactData && contactData.contact.business) ? contactData.contact.business : $site.location.title,
      'og:url': 'https://' + $site.host,
      // 'og:image': ($site.images && $site.images.logo) ? $site.images.logo.medium : ($site.images && $site.images.hero) ? $site.images.hero.medium : ''
    }

    return properties
  })

  const twitterCardProperties = computed(() => {
    const contactData = $site.tabs.contact

    const properties = {
      'twitter:card': 'summary',
      'twitter:title': (contactData && contactData.contact.business) ? contactData.contact.business : $site.location.title,
      'twitter:image': ($site.images && $site.images.logo) ? $site.images.logo.medium : ($site.images && $site.images.hero) ? $site.images.hero.medium : ''
    }

    if ($site.tabs.twitter && $site.tabs.twitter.username) {
      properties['twitter:site'] = '@' + $site.tabs.twitter.username
    }

    return properties
  })

  useHead({
    htmlAttrs: {
      lang: i18nHead.value.htmlAttrs!.lang
    },
    link: [
      ...(i18nHead.value.link || []),
      ...link.value
    ],
    meta: [
      ...(i18nHead.value.meta || []),
      ...meta.value
    ],
    title: $site.location.title,
    script: scripts.value,
    style: style.value,
    // __dangerouslyDisableSanitizersByTagID: {
    //   'ldjson-schema': ['innerHTML'],
    //   'root-styles': ['innerHTML']
    // }
  })

  /* export default defineNuxtComponent({

    scrollToTop: true,
    layout: function (context) {
      return context.store.getters.layout
    },
    transition (to, from) {
      const order = [
        '/',
        '/home',
        '/bot',

        '/book-now',
        '/timetable',
        '/last-minute',
        '/gift',
        '/gallery',
        '/reviews',

        '/customer',
        '/customer/reservation',
        '/customer/sale',
        '/customer/sale/payment',
        '/customer/waitlist',

        '/book-now/availability',
        '/book-now/detail',
        '/book-now/customer',
        '/book-now/confirm',
        '/book-now/complete',

        '/gift/card',
        '/gift/card/customer',
        '/gift/card/confirm',
        '/gift/card/complete',

        '/gift/pass',
        '/gift/pass/customer',
        '/gift/pass/confirm',
        '/gift/pass/complete'
      ]

      const toIndex = order.indexOf(to.path)
      const fromIndex = (from) ? order.indexOf(from.path) : -1

      let direction = 'right'

      if (fromIndex >= 0) {
        direction = (toIndex > fromIndex) ? 'left' : 'right'
      }

      return {
        mode: 'out-in',
        name: 'slide-' + direction
      }
    },
    computed: {
      layoutRouteReplace () {
        const layout = this.$store?.getters.layout
        return (layout === 'bot')
      },
      structuredData () {
        
      },
      ,
      siteStyles () {
        
      }
    },
    methods: {
      layoutRouteTo (route) {
        if (this.layoutRouteReplace) {
          this.$router.replace(route)
        } else {
          this.$router.push(route)
        }
      },
      scrollTo (element, duration, options) {
        const layout = this.$store?.getters.layout

        options = options || {}

        if (layout === 'bot') {
          options.container = document.getElementById('bot-content-container')
        }

        if (layout === 'default') {
          options.container = document.getElementById('main')
        }

        this.$scrollTo(element, duration, options)
      }
    }
  }) */
}